import React, { useState, useCallback } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { ModeProvider } from "./context/ModeContext";
import { ListPageCacheProvider } from "./context/ListPageCacheContext";
import Login from "./pages/userAuth/Login";
import Layout from "./hoc/Layout";
import Home from "./pages/home/Home";
import BOM from "./components/BOM";
import PackingList from "./components/PackingList";
import Loader from "./components/Loader";
import Charts from "./components/Charts";
import Reports from "./pages/report/Reports";
import Profile from "./pages/profile/Profile";
import Invoice from "./pages/invoice/Invoice";
import JobCard from "./pages/formBuilder/JobCard";
import TransactionInvoice from "./pages/invoice/TransactionInvoice";
import Settings from "./pages/settings/Settings";
import ProductMaster from "./components/master/ProductMaster";
import VendorMasterDataModal from "./components/master/VendorMasterDataModal";
import CustomerMasterDataModal from "./components/master/CustomerMasterDataModal";
import TabContentPage from "./pages/TabsDynamic/TabContentPage";
import TabContnetDetail from "./pages/TabsDynamic/TabContnetDetail";
import ShortCuts from "./components/ShortCuts";
import TermsAndConditions from "./components/TermsAndConditions";
import UserManual from "./components/UserManual";
import DynamicTransactionPage from "./pages/TabsDynamic/DynamicTransactionPage";
import useShortcutNavigation from "./utils/UseShortcutNavigation";
import Help from "./pages/help/Help";
import Tasks from "./pages/tasks/Tasks";
import { AssistantView } from "./pages/assistant/Assistant";
import HRPage from "./pages/hr/HRPage";
import MailboxPage from "./pages/mailbox/MailboxPage";
import MailboxSettingsPage from "./pages/mailbox/MailboxSettingsPage";
import ModuleLinks from "./components/ModuleLinks";
import PurchaseComparisonsPage from "./pages/purchase/PurchaseComparisonsPage";
import "./App.css";

// Stable across sidebar route refreshes: never remount an in-flight OAuth callback
// or persist its sensitive query in a login return URL.
const AuthenticatedModuleRoute = ({ children }) => {
  const { isLoggedIn, isLoading } = useAuth();
  if (isLoading) return <Loader />;
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return children;
};

const ShortcutNavigation = () => {
  useShortcutNavigation();
  return null;
};

const App = () => {
  const { isLoggedIn, isLoading, handleLogin, handleLogout } = useAuth();
  const [validRoutes, setValidRoutes] = useState(() => {
    const saved = localStorage.getItem("validRoutes");
    return saved ? JSON.parse(saved) : [];
  });

  const isValidRoute = useCallback(
    (path) => {
      if (!path) return true;
      if (path === "settings" || path === "profile") return true;
      return validRoutes.includes(path);
    },
    [validRoutes],
  );

  const handleSetValidRoutes = useCallback((routes) => {
    setValidRoutes(routes);
    localStorage.setItem("validRoutes", JSON.stringify(routes));
  }, []);

  const DynamicContentRoute = () => {
    const { tab, category } = useParams();

    if (!isValidRoute(tab)) {
      return <Navigate to="/" />;
    }

    return category ? <TabContnetDetail /> : <TabContentPage />;
  };

  const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    const { tab } = useParams();
    const { isLoggedIn, isLoading } = useAuth();

    if (isLoading) {
      return <Loader />;
    }

    if (!isLoggedIn) {
      const currentPath = location.pathname + location.search;
      if (currentPath !== "/login") {
        localStorage.setItem("redirectAfterLogin", currentPath);
      }
      return <Navigate to="/login" replace />;
    }

    if (tab && !isValidRoute(tab)) {
      return <Navigate to="/" />;
    }

    return children;
  };

  const MasterModalWrapper = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const handleBackClick = () => {
      navigate(-1);
    };

    return (
      <ProtectedRoute>
        {location.state?.transactionCode === "CM" ? (
          <CustomerMasterDataModal onClose={handleBackClick} />
        ) : location.state?.transactionCode === "VM" ? (
          <VendorMasterDataModal onClose={handleBackClick} />
        ) : (
          <ProductMaster onClose={handleBackClick} />
        )}
      </ProtectedRoute>
    );
  };

  const renderRoutes = () => {
    if (isLoading) {
      return <Loader />;
    }

    if (!isLoggedIn) {
      return (
        <Routes>
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      );
    }

    const DocumentRenderer = () => {
      const { documentFileName } = useParams();
      switch (documentFileName) {
        case "invoice":
          return <Invoice />;
        case "transactionInvoice":
          return <TransactionInvoice />;
        default:
          return <Invoice />;
      }
    };

    return (
      <ListPageCacheProvider>
        <ModeProvider>
          <Layout setValidRoutes={handleSetValidRoutes} onLogout={handleLogout}>
            <Routes>
              <Route path="/assistant" element={<AssistantView fullPage />} />
              <Route path="/" element={<><ModuleLinks /><Home /></>} />
              <Route path="/modules" element={<Navigate to="/" replace />} />
              <Route path="/purchase/comparisons" element={<AuthenticatedModuleRoute><PurchaseComparisonsPage /></AuthenticatedModuleRoute>} />
              <Route path="/modules/hr" element={<AuthenticatedModuleRoute><HRPage /></AuthenticatedModuleRoute>} />
              <Route path="/modules/mailbox" element={<AuthenticatedModuleRoute><MailboxPage /></AuthenticatedModuleRoute>} />
              <Route path="/modules/mailbox/settings" element={<AuthenticatedModuleRoute><MailboxSettingsPage /></AuthenticatedModuleRoute>} />
              <Route path="/mailbox" element={<AuthenticatedModuleRoute><MailboxPage /></AuthenticatedModuleRoute>} />
              <Route path="/mailbox/callback" element={<AuthenticatedModuleRoute><MailboxPage /></AuthenticatedModuleRoute>} />
              <Route
                path="/:tab/:category/charts"
                element={
                  <ProtectedRoute>
                    <Charts />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/graphical-reports/:formattedName"
                element={
                  <ProtectedRoute>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports/:formattedName"
                element={
                  <ProtectedRoute>
                    <Reports />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/master/:category"
                element={
                  <ProtectedRoute>
                    <TabContentPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:tab/:category/bom"
                element={
                  <ProtectedRoute>
                    <BOM />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:tab/:category/packing-list"
                element={
                  <ProtectedRoute>
                    <PackingList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/master/:category/transactions/:documentFileName"
                element={
                  <ProtectedRoute>
                    <DocumentRenderer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:tab/:category/transactions/:documentFileName"
                element={
                  <ProtectedRoute>
                    <DocumentRenderer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:tab/:category/transactions"
                element={
                  <ProtectedRoute>
                    <DynamicTransactionPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/master/:category/:subType"
                element={
                  <ProtectedRoute>
                    <TabContnetDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/master/:category/:subType/bom"
                element={
                  <ProtectedRoute>
                    <BOM />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/master/:category/:subType/:mode"
                element={<MasterModalWrapper />}
              />
              <Route
                path="/:tab"
                element={
                  <ProtectedRoute>
                    <DynamicContentRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:tab/:category"
                element={
                  <ProtectedRoute>
                    <DynamicContentRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:tab/:category/details"
                element={
                  <ProtectedRoute>
                    <TabContnetDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:tab/:category/job-card"
                element={
                  <ProtectedRoute>
                    <JobCard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/:tab/:category/:mode"
                element={<MasterModalWrapper />}
              />
              <Route
                path="/help"
                element={
                  <ProtectedRoute>
                    <Help />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/tasks"
                element={
                  <ProtectedRoute>
                    <Tasks />
                  </ProtectedRoute>
                }
              />

              <Route path="/shortcut-keys" element={<ShortCuts />} />
              <Route path="/user-manual" element={<UserManual />} />
              <Route
                path="/terms-conditions"
                element={<TermsAndConditions />}
              />
            </Routes>
          </Layout>
        </ModeProvider>
      </ListPageCacheProvider>
    );
  };

  return (
    <Router>
      <ShortcutNavigation />
      {renderRoutes()}
    </Router>
  );
};

export default App;
